import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

// VA Funding Fee Rates (2026) — percentage of loan amount
const FUNDING_FEE = {
  first_use: { down0: 2.15, down5: 1.50, down10: 1.25 },
  subsequent: { down0: 3.30, down5: 1.50, down10: 1.25 },
  reserve_first: { down0: 2.15, down5: 1.50, down10: 1.25 },
  reserve_subsequent: { down0: 3.30, down5: 1.50, down10: 1.25 },
};

function calculateMonthlyPayment(principal, annualRate, termYears) {
  if (principal <= 0 || annualRate <= 0 || termYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function calculateTotalInterest(monthlyPayment, termYears, principal) {
  return (monthlyPayment * termYears * 12) - principal;
}

const HomeLoanCalculator = () => {
  const [homePrice, setHomePrice] = useState(350000);
  const [downPayment, setDownPayment] = useState(0);
  const [interestRate, setInterestRate] = useState(6.25);
  const [loanTerm, setLoanTerm] = useState(30);
  const [firstUse, setFirstUse] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [conventionalRate, setConventionalRate] = useState(6.75);
  const [conventionalDown, setConventionalDown] = useState(20);

  // Split calculations into smaller memos for optimization
  const vaLoanDetails = useMemo(() => {
    const downPercent = (downPayment / homePrice) * 100;
    const loanAmount = homePrice - downPayment;

    let feeCategory;
    if (downPercent >= 10) feeCategory = 'down10';
    else if (downPercent >= 5) feeCategory = 'down5';
    else feeCategory = 'down0';

    const feeType = firstUse ? 'first_use' : 'subsequent';
    const feePercent = isDisabled ? 0 : (FUNDING_FEE[feeType]?.[feeCategory] || 2.15);
    const fundingFee = loanAmount * (feePercent / 100);
    const vaLoanTotal = loanAmount + fundingFee;
    const vaMonthly = calculateMonthlyPayment(vaLoanTotal, interestRate, loanTerm);
    const vaTotalInterest = calculateTotalInterest(vaMonthly, loanTerm, vaLoanTotal);

    return {
      loanAmount, fundingFee, feePercent, vaLoanTotal,
      vaMonthly: Math.round(vaMonthly * 100) / 100,
      vaTotalInterest: Math.round(vaTotalInterest),
      vaTotalCost: Math.round(vaLoanTotal + vaTotalInterest),
    };
  }, [homePrice, downPayment, interestRate, loanTerm, firstUse, isDisabled]);

  const convLoanDetails = useMemo(() => {
    const convDown = homePrice * (conventionalDown / 100);
    const convLoan = homePrice - convDown;
    const convMonthly = calculateMonthlyPayment(convLoan, conventionalRate, loanTerm);
    const pmiMonthly = conventionalDown < 20 ? convLoan * 0.005 / 12 : 0;
    const convTotalPayment = convMonthly + pmiMonthly;
    const convTotalInterest = calculateTotalInterest(convMonthly, loanTerm, convLoan);

    return {
      convDown, convLoan,
      convMonthly: Math.round(convMonthly * 100) / 100,
      pmiMonthly: Math.round(pmiMonthly * 100) / 100,
      convTotalPayment: Math.round(convTotalPayment * 100) / 100,
      convTotalInterest: Math.round(convTotalInterest),
    };
  }, [homePrice, conventionalRate, conventionalDown, loanTerm]);

  const results = useMemo(() => {
    const monthlySavings = convLoanDetails.convTotalPayment - vaLoanDetails.vaMonthly;
    const lifetimeSavings = (monthlySavings * loanTerm * 12) + convLoanDetails.convDown - downPayment - vaLoanDetails.fundingFee;

    return {
      ...vaLoanDetails,
      ...convLoanDetails,
      monthlySavings: Math.round(monthlySavings * 100) / 100,
      lifetimeSavings: Math.round(lifetimeSavings),
    };
  }, [vaLoanDetails, convLoanDetails, loanTerm, downPayment]);

  return (
    <div className="space-y-6" data-testid="home-loan-calculator">
      {/* VA Loan Inputs */}
      <div>
        <Label className="text-sm font-semibold text-slate-800 mb-2 block">VA Loan Details</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Home Price</Label>
            <Input
              type="number"
              min={0}
              value={homePrice}
              onChange={(e) => setHomePrice(Number(e.target.value))}
              className="mt-1"
              data-testid="home-price"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Down Payment</Label>
            <Input
              type="number"
              min={0}
              value={downPayment}
              onChange={(e) => setDownPayment(Number(e.target.value))}
              className="mt-1"
              data-testid="down-payment"
            />
            <p className="text-[10px] text-green-600 mt-0.5">VA: $0 down OK</p>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Interest Rate (%)</Label>
            <Input
              type="number"
              step={0.125}
              min={0}
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="mt-1"
              data-testid="va-rate"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Loan Term</Label>
            <select
              value={loanTerm}
              onChange={(e) => setLoanTerm(Number(e.target.value))}
              className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            >
              <option value={15}>15 Years</option>
              <option value={30}>30 Years</option>
            </select>
          </div>
        </div>
        <div className="flex gap-4 mt-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={firstUse} onChange={(e) => setFirstUse(e.target.checked)} className="rounded" />
            First-time use
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={isDisabled} onChange={(e) => setIsDisabled(e.target.checked)} className="rounded" />
            10%+ VA disability (waives funding fee)
          </label>
        </div>
      </div>

      {/* Conventional Comparison Inputs */}
      <div>
        <Label className="text-sm font-semibold text-slate-800 mb-2 block">Conventional Comparison</Label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Conv. Interest Rate (%)</Label>
            <Input
              type="number"
              step={0.125}
              min={0}
              value={conventionalRate}
              onChange={(e) => setConventionalRate(Number(e.target.value))}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Conv. Down Payment (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={conventionalDown}
              onChange={(e) => setConventionalDown(Number(e.target.value))}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Side-by-side Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-blue-800 mb-3">VA Loan</h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-600">Loan Amount:</span><span className="font-semibold">${results.loanAmount.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Funding Fee ({results.feePercent}%):</span><span className="font-semibold">${results.fundingFee.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Total Financed:</span><span className="font-semibold">${results.vaLoanTotal.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">PMI:</span><span className="font-semibold text-green-600">$0 (No PMI!)</span></div>
              <div className="flex justify-between pt-1.5 border-t"><span className="text-slate-600">Down Payment:</span><span className="font-semibold">${downPayment.toLocaleString()}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-blue-200">
                <span className="font-semibold text-blue-800">Monthly Payment:</span>
                <span className="text-lg font-bold text-blue-700" data-testid="va-monthly">${results.vaMonthly.toLocaleString()}</span>
              </div>
              <div className="flex justify-between"><span className="text-slate-600">Total Interest:</span><span className="font-semibold">${results.vaTotalInterest.toLocaleString()}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Conventional</h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-600">Loan Amount:</span><span className="font-semibold">${results.convLoan.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Funding Fee:</span><span className="font-semibold">$0</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Total Financed:</span><span className="font-semibold">${results.convLoan.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">PMI:</span><span className={`font-semibold ${results.pmiMonthly > 0 ? 'text-red-600' : 'text-green-600'}`}>{results.pmiMonthly > 0 ? `$${results.pmiMonthly.toLocaleString()}/mo` : '$0'}</span></div>
              <div className="flex justify-between pt-1.5 border-t"><span className="text-slate-600">Down Payment:</span><span className="font-semibold">${results.convDown.toLocaleString()}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-slate-200">
                <span className="font-semibold text-slate-700">Monthly Payment:</span>
                <span className="text-lg font-bold text-slate-700">${results.convTotalPayment.toLocaleString()}</span>
              </div>
              <div className="flex justify-between"><span className="text-slate-600">Total Interest:</span><span className="font-semibold">${results.convTotalInterest.toLocaleString()}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Savings Summary */}
      <Card className={`${results.monthlySavings > 0 ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-600 mb-0.5">VA Loan Monthly Savings</p>
            <p className={`text-2xl font-bold ${results.monthlySavings > 0 ? 'text-green-700' : 'text-amber-700'}`}>
              {results.monthlySavings > 0 ? '+' : ''}{results.monthlySavings < 0 ? '-' : ''}${Math.abs(results.monthlySavings).toLocaleString()}/mo
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-600 mb-0.5">Estimated Lifetime Savings</p>
            <p className={`text-2xl font-bold ${results.lifetimeSavings > 0 ? 'text-green-700' : 'text-amber-700'}`} data-testid="lifetime-savings">
              ${Math.abs(results.lifetimeSavings).toLocaleString()}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 p-2.5 rounded-lg border border-blue-100">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>VA loans require no down payment and no PMI. The funding fee (waived for 10%+ disability) can be financed into the loan. VA loan rates are typically 0.25-0.50% lower than conventional.</span>
      </div>
    </div>
  );
};

export default HomeLoanCalculator;
